import React, { useCallback, useEffect, useState } from 'react';
import { Card, Form, Input, Select, Switch, Button, Typography, message, Skeleton, Descriptions, Tag } from 'antd';
import { UserOutlined, ReloadOutlined, MailOutlined, GlobalOutlined, SaveOutlined } from '@ant-design/icons';
import MainLayout from '../../../layouts/MainLayout';
import { superadminProfileSettingsApi, userProfileApi, show422Error } from '../../../utils/api';
import { useAuth } from '../../../contexts/AuthContext';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';

const { Text } = Typography;

const SuperadminProfileSettings = () => {
  const { user: authUser } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superadminProfileSettingsApi.get();
      const root = res?.data || {};
      const u = root.user || {};
      setUser(u);
      setCountryOptions(root.countries || []);
      form.setFieldsValue({
        name: u.name || '',
        email: u.email || '',
        country_id: u.country_id ?? undefined,
        email_notifications: Number(u.email_notifications) === 1,
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت پروفایل');
      setUser(null);
      setCountryOptions([]);
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmit = async (values) => {
    const uid = user?.id ?? authUser?.id;
    if (!uid) {
      message.error('شناسه کاربر یافت نشد');
      return;
    }
    const payload = {
      name: String(values.name || '').trim(),
      email: String(values.email || '').trim(),
      country_id: values.country_id,
      email_notifications: values.email_notifications ? 1 : 0,
    };
    setSubmitting(true);
    try {
      await userProfileApi.update(uid, payload);
      message.success('پروفایل ذخیره شد');
      fetchData();
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-xl bg-violet-50 dark:bg-violet-900/25">
                <UserOutlined className="text-lg sm:text-xl text-violet-600 dark:text-violet-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100">تنظیمات پروفایل</h1>
                <Text type="secondary" className="text-xs sm:text-sm">
                  اطلاعات حساب سوپرادمین
                </Text>
              </div>
            </div>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              بروزرسانی
            </Button>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            {loading ? (
              <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800">
                <Skeleton active paragraph={{ rows: 6 }} />
              </Card>
            ) : !user ? (
              <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800">
                <Text type="secondary">اطلاعات کاربر یافت نشد</Text>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800 lg:col-span-1">
                  <Descriptions column={1} size="small" className="custom-font">
                    <Descriptions.Item label="شناسه">{user.id}</Descriptions.Item>
                    <Descriptions.Item label="نام">{user.name || '—'}</Descriptions.Item>
                    <Descriptions.Item label="ایمیل">
                      <span dir="ltr">{user.email || '—'}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="اعلان ایمیل">
                      <Tag color={Number(user.email_notifications) === 1 ? 'green' : 'default'}>
                        {Number(user.email_notifications) === 1 ? 'فعال' : 'غیرفعال'}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                <Card
                  className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800 lg:col-span-2"
                  title={<span style={{ fontFamily: 'CustomFont' }}>ویرایش پروفایل</span>}
                >
                  <Form form={form} layout="vertical" onFinish={onSubmit}>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Form.Item name="name" label="نام" rules={[{ required: true, message: 'نام را وارد کنید' }]}>
                        <Input prefix={<UserOutlined className="text-gray-400" />} />
                      </Form.Item>
                      <Form.Item
                        name="email"
                        label="ایمیل"
                        rules={[
                          { required: true, message: 'ایمیل را وارد کنید' },
                          { type: 'email', message: 'ایمیل معتبر نیست' },
                        ]}
                      >
                        <Input dir="ltr" prefix={<MailOutlined className="text-gray-400" />} />
                      </Form.Item>
                      <Form.Item name="country_id" label="کشور" className="sm:col-span-2">
                        <Select
                          showSearch
                          allowClear
                          optionFilterProp="label"
                          popupClassName="custom-font"
                          placeholder="انتخاب کشور"
                          suffixIcon={<GlobalOutlined />}
                          options={countryOptions.map((c) => ({
                            value: c.id,
                            label: c.nicename || c.name,
                          }))}
                        />
                      </Form.Item>
                      <Form.Item name="email_notifications" label="اعلان‌های ایمیل" valuePropName="checked">
                        <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
                      </Form.Item>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={submitting}
                        className={PANEL_PRIMARY_BTN_CLASS}
                      >
                        ذخیره تغییرات
                      </Button>
                    </div>
                  </Form>
                </Card>
              </div>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SuperadminProfileSettings;
